/**
 * Ren kartgeometri som delas av den platta kartan (Leaflet) och 3D-vyn (MapLibre):
 * avståndsringar, vittringskon, kompassutjämning och Lantmäteriets fastighetsgränser.
 * Koordinater är [lat, lng] om inget annat sägs.
 */

const EARTH_R = 6371000;

/** Punkt från origo i given riktning (grader, 0 = norr) och avstånd (meter). */
export function destPoint(lat: number, lng: number, bearingDeg: number, distMeters: number): [number, number] {
  const b = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;
  const d = distMeters / EARTH_R;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b));
  const lng2 = lng1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return [(lat2 * 180) / Math.PI, (lng2 * 180) / Math.PI];
}

// ── Avståndsringar ────────────────────────────────────────────────────────────

/** Jämna avstånd (meter) efter Leaflet-zoomnivå. MapLibre-zoom är en nivå lägre (512-plattor). */
export function getDistanceRings(zoom: number): number[] {
  if (zoom >= 18) return [25, 50, 100];
  if (zoom >= 16) return [50, 100, 250];
  if (zoom >= 15) return [100, 250, 500];
  if (zoom >= 14) return [200, 500, 1000];
  if (zoom >= 13) return [500, 1000, 2000];
  if (zoom >= 12) return [1000, 2500, 5000];
  if (zoom >= 10) return [2000, 5000, 10000];
  return [5000, 10000, 25000];
}

export const formatDistance = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

/** Ringens opacitet: innerst tydligast. */
export const ringOpacity = (i: number) => Math.max(0.7 - i * 0.1, 0.4);

/** Var ringens siffra sitter: ringens nordligaste punkt. */
export function ringLabelPoint(pos: { lat: number; lng: number }, dist: number): [number, number] {
  return [pos.lat + (dist / EARTH_R) * (180 / Math.PI), pos.lng];
}

/** Sluten cirkel som [lng, lat] (GeoJSON-ordning). */
export function circleLngLat(lat: number, lng: number, r: number, steps = 64): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    out.push([lng + (r * Math.cos(a)) / (111320 * Math.cos((lat * Math.PI) / 180)), lat + (r * Math.sin(a)) / 111320]);
  }
  return out;
}

// ── Vittring ──────────────────────────────────────────────────────────────────

export const VITTRING_COLOR = '#d4b84a';
export const VITTRING_LAYERS = 12;

/** Hur långt vittringen når (meter) vid given vindstyrka (m/s). */
export const vittringDistance = (windSpd: number) => Math.min(600, Math.max(150, 150 + windSpd * 30));

/**
 * Vittringskonen nedvinds från origo som 12 lager: de yttre breda och nästan genomskinliga,
 * de inre smalare och lite tätare. Mjukheten kommer av lagren, aldrig av CSS-filter.
 * `windDir` är varifrån det blåser (grader, meteorologisk riktning).
 */
export function vittringCone(origin: { lat: number; lng: number }, windDir: number, windSpd: number | null): { pts: [number, number][]; op: number }[] {
  const downwind = (windDir + 180) % 360;
  const distance = vittringDistance(windSpd ?? 3);
  // Täta punkter (1,5°) och en rundad spets nära jägaren.
  const buildArc = (halfAng: number, distM: number): [number, number][] => {
    const pts: [number, number][] = [];
    const tip = Math.max(12, distM * 0.04);
    for (let a = halfAng; a >= -halfAng; a -= 1.5) {
      // Bakre bågen runt jägaren (rundad spets) i stället för en enda hörnpunkt.
      pts.push(destPoint(origin.lat, origin.lng, (downwind + 180 + a * 2 + 360) % 360, tip));
    }
    for (let a = -halfAng; a <= halfAng; a += 1.5) {
      // Ytterkanten avrundas mot sidorna så att bågen inte slutar i ett hörn.
      const edge = 1 - 0.35 * Math.pow(Math.abs(a) / halfAng, 3);
      pts.push(destPoint(origin.lat, origin.lng, (downwind + a + 360) % 360, distM * edge));
    }
    return pts;
  };
  return Array.from({ length: VITTRING_LAYERS }, (_, i) => {
    const f = i / (VITTRING_LAYERS - 1); // 0 = ytterst, 1 = innerst
    return { pts: buildArc(46 - 36 * f, distance * (1.04 - 0.3 * f)), op: 0.045 + 0.04 * f };
  });
}

// ── GPS-brus ──────────────────────────────────────────────────────────────────

type LatLng = { lat: number; lng: number };

/** Sant om positionen har flyttats minst `meters` (eller gått från/till okänd). Avstånd med haversine. */
export function movedAtLeast(a: LatLng | null, b: LatLng | null, meters: number): boolean {
  if (!a || !b) return a !== b;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.sqrt(h)) >= meters;
}

/**
 * Behöver vittringskonen ritas om? Telefonens GPS skickar ny position ungefär varje sekund även när man
 * står still; konen byggs bara om när vinden ändrats eller origo flyttats minst `minMoveM` meter.
 */
export function vittringChanged(
  prev: { origin: LatLng; windDir: number; windSpd: number | null } | null,
  next: { origin: LatLng; windDir: number; windSpd: number | null } | null,
  minMoveM = 5,
): boolean {
  if (!prev || !next) return prev !== next;
  return prev.windDir !== next.windDir || prev.windSpd !== next.windSpd || movedAtLeast(prev.origin, next.origin, minMoveM);
}

// ── Kompass ───────────────────────────────────────────────────────────────────

/** Kortaste vinkelskillnad target − current i (−180, 180]. */
export function angleDelta(current: number, target: number): number {
  const d = (((target - current) % 360) + 540) % 360 - 180;
  return d === -180 ? 180 : d;
}

/**
 * Ett steg mot kompassriktningen: exponentiell utjämning över tid (tau ms) längs kortaste vägen,
 * med ett dödband så att kartan står still när telefonen ligger still och sensorn brusar.
 */
export function smoothBearing(current: number, target: number, dtMs: number, tauMs = 180, deadbandDeg = 0.4): number {
  const d = angleDelta(current, target);
  if (Math.abs(d) < deadbandDeg) return current;
  const k = 1 - Math.exp(-Math.max(0, dtMs) / tauMs);
  return (((current + d * k) % 360) + 360) % 360;
}

// ── Kartvinkel (3D) ───────────────────────────────────────────────────────────

/** Största lutning i 3D-vyn (grader) och den vy kartan lutar in till vid start och med "Luta". */
export const PITCH_MAX = 80;
export const PITCH_TILTED = 62;

export const clampPitch = (p: number) => Math.round(Math.min(PITCH_MAX, Math.max(0, p)));

/** Lutning från en punkt på det lodräta reglaget: överst = PITCH_MAX, nederst = platt. */
export function pitchFromTrack(y: number, trackTop: number, trackHeight: number): number {
  if (trackHeight <= 0) return 0;
  return clampPitch((1 - (y - trackTop) / trackHeight) * PITCH_MAX);
}

/** Ny lutning för en tangent på reglaget, eller null om tangenten inte gäller det. */
export function pitchForKey(pitch: number, key: string): number | null {
  switch (key) {
    case 'ArrowUp': case 'ArrowRight': return clampPitch(pitch + 5);
    case 'ArrowDown': case 'ArrowLeft': return clampPitch(pitch - 5);
    case 'PageUp': return clampPitch(pitch + 20);
    case 'PageDown': return clampPitch(pitch - 20);
    case 'Home': return 0;
    case 'End': return PITCH_MAX;
    default: return null;
  }
}

// ── Fastighetsgränser (Lantmäteriet WMS) ──────────────────────────────────────

export const FASTIGHET_WMS_URL = 'https://minkarta.lantmateriet.se/map/fastighetsindelning?';

/** SLD för rosa streckade gränser (Lantmäteriets stil). */
export const FASTIGHET_GRANSER_SLD = `<StyledLayerDescriptor version="1.0.0">
          <NamedLayer>
            <Name>granser</Name>
            <UserStyle>
              <FeatureTypeStyle>
                <Rule>
                  <LineSymbolizer>
                    <Stroke>
                      <CssParameter name="stroke">#E91E63</CssParameter>
                      <CssParameter name="stroke-width">2</CssParameter>
                      <CssParameter name="stroke-dasharray">10 6</CssParameter>
                    </Stroke>
                  </LineSymbolizer>
                </Rule>
              </FeatureTypeStyle>
            </UserStyle>
          </NamedLayer>
        </StyledLayerDescriptor>`;

/** Plattmall för MapLibre (samma parametrar som Leaflets tileLayer.wms skickar). */
export function fastighetWmsTileUrl(layer: 'granser' | 'text'): string {
  const params = new URLSearchParams({
    service: 'WMS', request: 'GetMap', version: '1.1.1', layers: layer, styles: '',
    format: 'image/png', transparent: 'true', width: '256', height: '256', srs: 'EPSG:3857',
  });
  if (layer === 'granser') params.set('sld_body', FASTIGHET_GRANSER_SLD);
  // {bbox-epsg-3857} får inte URL-kodas.
  return `${FASTIGHET_WMS_URL}${params.toString()}&bbox={bbox-epsg-3857}`;
}

// ── Kompassläget i 2D (QV2) ────────────────────────────────────────────────────

/**
 * Den platta kartan vrids i kompassläget med CSS (`rotate(-heading)`) kring kartans mitt. Nålarna vrids tillbaka
 * med lika mycket kring sin spets (CSS-variabeln nedan sätts på kartans div), så att de och etiketterna står
 * upprätt och spetsen ligger kvar på sin position.
 */
export const UPRIGHT_VAR = '--qv-upright';
export const COMPASS_TRANSITION = 'transform 0.3s ease-out';

/** Stil för en nål vars ankare (spetsen) är (x, y) px från ikonens övre vänstra hörn. */
export function uprightStyle(x: number, y: number): string {
  return `transform:rotate(var(${UPRIGHT_VAR},0deg));transform-origin:${x}px ${y}px;transition:${COMPASS_TRANSITION}`;
}

/** Vrider punkten p (skärm-px, y nedåt) `deg` grader medurs kring c, som CSS rotate(). */
export function rotateAround(p: [number, number], c: [number, number], deg: number): [number, number] {
  const r = (deg * Math.PI) / 180;
  const dx = p[0] - c[0];
  const dy = p[1] - c[1];
  return [c[0] + dx * Math.cos(r) - dy * Math.sin(r), c[1] + dx * Math.sin(r) + dy * Math.cos(r)];
}

/**
 * Var en punkt i en nålikon hamnar på skärmen i kompassläget: ikonens punkt `iconPt` (px från ikonens hörn),
 * ikonens ankare `anchor` på kartpixeln `at`; kartan vrids `-heading` kring `center` och ikonen `+heading`
 * kring `origin` (ska vara ankaret).
 */
export function pinPointOnScreen(at: [number, number], anchor: [number, number], iconPt: [number, number], origin: [number, number], center: [number, number], heading: number): [number, number] {
  const corner: [number, number] = [at[0] - anchor[0], at[1] - anchor[1]];
  const inIcon = rotateAround([corner[0] + iconPt[0], corner[1] + iconPt[1]], [corner[0] + origin[0], corner[1] + origin[1]], heading);
  return rotateAround(inIcon, center, -heading);
}
